import React from 'react';
import { Box, Grid, Skeleton, CircularProgress, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const WidgetLoadingSkeleton = ({ layout = 'cards', showHeader = true, showAddButton = true, placeholderCount = 6 }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const columnCount = isLgUp ? 3 : isSmUp ? 2 : 1;

  if (layout === 'list') {
    const placeholderCountList = Math.max(4, columnCount * 2);
    return (
      <Box sx={{ mt: 1 }}>
        {showHeader && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={18} />
              <Box>
                <Skeleton variant="text" width={140} height={28} />
              </Box>
            </Box>
            {showAddButton && <Skeleton variant="circular" width={36} height={36} />}
          </Box>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`, gap: 12 }}>
          {Array.from({ length: placeholderCountList }).map((_, i) => (
            <Box key={i} sx={{ p: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                <Skeleton variant="rectangular" width={80} height={18} />
              </Box>
              <Box sx={{ position: 'relative' }}>
                <Skeleton variant="rectangular" height={96} animation="wave" sx={{ borderRadius: 1, mb: 1 }} />
                <Box sx={{
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-110%',
                    width: '35%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                    transform: 'skewX(-12deg)',
                    animation: 'widgetShimmer 1.6s linear infinite'
                  },
                  '@keyframes widgetShimmer': {
                    '0%': { left: '-110%' },
                    '100%': { left: '110%' }
                  }
                }} />
              </Box>
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="40%" />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Card layout
  return (
    <Box sx={{ py: 1 }}>
      {showHeader && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} color="inherit" />
            <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {t('widgets.loading', 'Loading widgets')}
            </Typography>
          </Box>
          {showAddButton && <Skeleton variant="circular" width={36} height={36} />}
        </Box>
      )}

      <Grid container spacing={2}>
        {Array.from({ length: placeholderCount }).map((_, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Box sx={{
              borderRadius: 2,
              overflow: 'hidden',
              background: '#fff',
              p: 1.5,
              boxShadow: '0 8px 24px rgba(87,87,176,0.06)',
              position: 'relative'
            }}>
              <Skeleton variant="rectangular" height={16} width="55%" sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={120} animation="wave" sx={{ borderRadius: 1, mb: 1 }} />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="40%" />

              <Box sx={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-110%',
                  width: '40%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                  transform: 'skewX(-15deg)',
                  animation: 'widgetShimmer 1.6s linear infinite'
                },
                '@keyframes widgetShimmer': {
                  '0%': { left: '-110%' },
                  '100%': { left: '110%' }
                }
              }} />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default WidgetLoadingSkeleton;
